import { useRef } from "react";
import moment from 'moment'
import { Plus } from "react-feather";
import { Row, Col } from 'reactstrap';
import { Image } from "../../../AbstractElements";
import { XCircle } from "react-feather";
import { dynamicImage } from "../../../Service";
import { Button } from "reactstrap";

interface ASDTemplateProps {
  onASDChange?: any
  asdContent?: any
  fullName?: string
  dob?: string
  address?: string
  phoneNumber?: string
  parents?: string
  handleUpload?: any
  logoUploaded?: any
  logoUrl?: any
  logoDimensions?: any
  signDimensions?: any
  signUploaded?: any
  signUrl?: any
  rewriteStatus?: any
  patient?: any,
  generateSummary?: any
  handleFileChange?: any
  handleDeleteDoc?: any
  handleClickDoc?: any
  handleUploadClick?: any,
  documents?: any
}

const ASDTemplate = ({
  onASDChange,
  asdContent,
  fullName,
  parents,
  handleUpload,
  logoUploaded,
  logoUrl,
  logoDimensions,
  signDimensions,
  signUploaded,
  signUrl,
  rewriteStatus,
  patient,
  generateSummary,
  handleFileChange,
  handleDeleteDoc,
  handleClickDoc,
  documents
}: ASDTemplateProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  const displayAge = (dob: string) => {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if the birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="d-flex flex-column gap-3 mt-4">
      <div className="d-flex justify-content-between w-100">
        <div className="fw-bold">
          <div>WITHOUT PREJUDICE</div>
          <div>DIAGNOSTIC ASSESSMENT FOR</div>
          <div>AUTISM SPECTRUM DISORDER</div>
        </div>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ border: "1px dashed", width: '250px', height: '90px', cursor: 'pointer' }}
          onClick={() => logoInputRef.current?.click()}>
          <div>
            {logoUploaded
              ? <img
                src={logoUrl}
                alt="Uploaded logo"
                style={{ width: logoDimensions.width, height: logoDimensions.height }}
              />
              : <Plus />}
          </div>
          <input
            ref={logoInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: "none" }}
            onChange={(e) => handleUpload(e, "logo")}
          />
        </div>
      </div>
      <div className="w-100 asdTemplate gap-3 w-100">
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">Name:</span> {`${patient?.firstname || ""} ${patient?.lastname || ""}`}</div>
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">DOB:</span> {moment(patient?.birthday).format("YYYY-MM-DD")}</div>
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">Age:</span>{` ${displayAge(patient?.birthday)} years old`}</div>
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">Phone:</span>{patient?.phonenumber}</div>
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">Address:</span>{patient?.address}</div>
        {
          patient?.type === "child" && <div className="d-flex align-items-center gap-2"><span className="fw-bold">School:</span>{patient?.school}</div>
        }
        {
          patient?.type === "child" && <div className="d-flex align-items-center gap-2"><span className="fw-bold">Parent:</span>{`${patient?.parent_firstname} ${patient?.parent_lastname}`}</div>
        }
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">Date of Report:</span>{moment(Date.now()).format("YYYY-MM-DD")}</div>
        <div className="d-flex align-items-center gap-2"><span className="fw-bold">Date Seen:</span>
          <input className="border" type="date" style={{ border: "1px dashed", padding: '0px 10px', borderRadius: '4px', outline: 'none' }} value={asdContent.dateSeen} name="dateSeen" onChange={(e: any) => onASDChange(e.target)} />
        </div>
      </div>
      <div className="w-100 mt-3">
      </div>
      <div className="w-100">
        <div className="fw-bold">INFORMED CONSENT AND ASSENT</div>
        <div className="mt-2">
          {`${patient?.firstname} ${patient?.lastname} and his parents was fully informed about the purpose of the assessment, the components involved, and the use of the results. It was clearly communicated that this assessment is part of a consensus diagnostic pathway, meaning that the findings would be preliminary and would require further review by a collaborating Developmental Paediatrician for confirmation if needed.`}
          <br />
          <br />
          {`${patient?.firstname} ${patient?.lastname}'s parents confirmed their understanding of this process and signed a Consent Form, acknowledging that they consented to the administration of the assessment and the generation of a report. They also agreed that the report could be shared with them and the referring professional (if applicable) as part of the consensus pathway.`}
          <br />
          <br />
          This report was prepared for the purpose of the client's clinical and/or educational management. The report is not intended for, and is unsuitable for, use in legal proceedings. <br />
          The information contained in this report is sensitive and confidential and must be treated accordingly. The results should only be interpreted by an appropriately trained professional.
        </div>
      </div>
      <div className="w-100">
        <div className="fw-bold">Current History Elicited Today:</div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={7}
            name="asdCurrentHistory"
            value={asdContent.asdCurrentHistory}
            onChange={(e: any) => onASDChange(e.target)}
          />
        </div>
      </div>
      <div className="w-100">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div className="fw-bold">Report feedback:</div>
          <Button
            color={`secondary`}
            className="d-flex align-items-center px-2 py-1"
            style={{ gap: '4px', cursor: asdContent.reportSummary || !documents?.length ? 'not-allowed' : 'pointer' }}
            onClick={generateSummary}
            disabled={asdContent.reportSummary || !documents?.length ? true : false}
          >
            Summarise
          </Button>
        </div>
        <div style={{ marginTop: '10px' }}>(You can upload maximum 4 documents)</div>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <div style={{ display: 'flex', marginTop: '0px' }}>
            {documents?.map((document: any, key: any) => (
              <div key={key} style={{ position: 'relative', width: '120px' }}>
                <div style={{ position: 'absolute', right: '8px', top: '0px', cursor: 'pointer' }} onClick={() => handleDeleteDoc(document)}>
                  <XCircle />
                </div>
                <Col className="p-2">
                  <Image className="img-fluid for-light" style={{ cursor: "pointer" }} src={dynamicImage("logo/PDF.png")} alt="doc" width={160} onClick={() => handleClickDoc(document.id)} />
                  <div className="ps-2" style={{ wordBreak: 'break-word' }}>{document.filename.split("__")[1]}</div>
                </Col>
              </div>
            ))}
            <div className='m-2 d-flex align-items-center justify-content-center' style={{ cursor: "pointer", width: '90px', height: '103px', border: '1px solid #dee2e6', borderRadius: '4px' }}
              onClick={handleUploadClick}
            >
              <Plus />
            </div>
          </div>
        </div>
        <div className="mt-2" dangerouslySetInnerHTML={{ __html: asdContent.reportSummary && asdContent.reportSummary.replaceAll(/\n/g, '<br />') }}></div>
      </div>
      <div className="w-100">
        <div className="fw-bold">Developmental History:</div>
      </div>
      <div className="w-100">
        <div>Pregnancy and Birth History:</div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdDevPregnancy"
            value={asdContent.asdDevPregnancy}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
      </div>
      <div className="w-100">
        <div>Gross Motor Skills:</div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdDevGross"
            value={asdContent.asdDevGross}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
      </div>
      <div className="w-100">
        <div>Fine Motor Skills:</div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdDevFineMotor"
            value={asdContent.asdDevFineMotor}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
      </div>
      <div className="w-100">
        <div>Speech and Language Development:</div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdDevSpeech"
            value={asdContent.asdDevSpeech}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
      </div>
      <div className="w-100">
        <div className="fw-bold">DSM-5 Criteria for Autism Spectrum Discorder:</div>
        <div className="mt-2">
          {/* <div>{`The following criteria are addressed below for, based on observation and interview with ${fullName}`}</div> */}
          <div>A criterion is considered met if there is sufficient evidence to support a rating of "2" or "3" based on the following system:</div>
          <br />
          <div>0 = Criterion Not Met</div>
          <div>1 = Criterion Partially Met</div>
          <div>2 = Criterion Met</div>
          <div>3 - Criterion Met to an Extreme Degree</div>
          <div>Please not this rating system is only for the purposes of assigning a rating to each individual criterion, and the “Severity Ratings” for each DSM-5 Section are noted in the table in the summary section of the report</div>
        </div>
      </div>
      <div>
        <div className="fw-bold" style={{ width: "80%", textAlign: 'center', margin: 'auto' }}>A. Persistent deficits in social communication and social interaction across multiple contexts, as manifested by the following, currently or by history:</div>
        <div className="mt-3">
          1. Deficits in social-emotional reciprocity; ranging from abnormal social approach and failure of normal back-and-forth conversation; to reduced sharing of interests, emotions, or affect; to failure to initiate or respond to social interactions.
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdSocialEmotional"
            value={asdContent.asdSocialEmotional}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSocialCreterion", value: "Met" })}>
            <input name="asdSocialCreterion" type="checkbox" value="Met" checked={asdContent.asdSocialCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSocialCreterion", value: "Not Met" })}>
            <input name="asdSocialCreterion" type="checkbox" value="Not Met" checked={asdContent.asdSocialCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>
        <div className="mt-3">
          2. Deficits in nonverbal communicative behaviours used for social interaction, ranging from poorly integrated verbal and nonverbal communication, to abnormalities in eye contact and body language, or deficits in understanding and use of gestures, to a total lack of facial expression and nonverbal communication.
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdNonverbal"
            value={asdContent.asdNonverbal}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdNonverbalCreterion", value: "Met" })}>
            <input name="asdNonverbalCreterion" type="checkbox" value="Met" onChange={(e) => onASDChange(e)} checked={asdContent.asdNonverbalCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdNonverbalCreterion", value: "Not Met" })}>
            <input name="asdNonverbalCreterion" type="checkbox" value="Not Met" checked={asdContent.asdNonverbalCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>
        <div className="mt-3">
          3. Deficits in developing, maintaining, and understanding relationships, ranging from difficulties adjusting behaviour to suit various social contexts; to difficulties in sharing imaginative play or in making friends; to absence of interest in peers.
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdDeveloping"
            value={asdContent.asdDeveloping}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdDevelopingCreterion", value: "Met" })}>
            <input name="asdDevelopingCreterion" type="checkbox" value="Met" checked={asdContent.asdDevelopingCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdDevelopingCreterion", value: "Not Met" })}>
            <input name="asdDevelopingCreterion" type="checkbox" value="Not Met" checked={asdContent.asdDevelopingCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>

        <div className="fw-bold mt-4" style={{ width: "80%", textAlign: 'center', margin: 'auto' }}>B. Restricted, repetitive patterns of behaviour, interests, or activities, as manifested by at least two of the following, currently or by history:</div>
        <div className="mt-3">
          1. Stereotyped or repetitive motor movements, use of objects, or speech (e.g. simple motor stereotypies, lining up toys or flipping objects, echolalia, idiosyncratic phrases)
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdStereotyped"
            value={asdContent.asdStereotyped}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdStereotypedCreterion", value: "Met" })}>
            <input name="asdStereotypedCreterion" type="checkbox" value="Met" checked={asdContent.asdStereotypedCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdStereotypedCreterion", value: "Not Met" })}>
            <input name="asdStereotypedCreterion" type="checkbox" value="Not Met" checked={asdContent.asdStereotypedCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>
        <div className="mt-3">
          2. Insistence on sameness, inflexible adherence to routines, or ritualised patterns of verbal or nonverbal behaviour (e.g. extreme distress at small changes, difficulties with transitions, rigid thinking patterns, greeting rituals, need to take same route or eat same food every day).
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdInsistence"
            value={asdContent.asdInsistence}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdInsistenceCreterion", value: "Met" })}>
            <input name="asdInsistenceCreterion" type="checkbox" value="Met" checked={asdContent.asdInsistenceCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdInsistenceCreterion", value: "Not Met" })}>
            <input name="asdInsistenceCreterion" type="checkbox" value="Not Met" checked={asdContent.asdInsistenceCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>
        <div className="mt-3">
          3. Highly restricted, fixated interests that are abnormal in intensity or focus (e.g. strong attachment to or preoccupation with unusual objects, excessively circumscribed or perseverative interests).
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdHighly"
            value={asdContent.asdHighly}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHighlyCreterion", value: "Met" })}>
            <input name="asdHighlyCreterion" type="checkbox" value="Met" checked={asdContent.asdHighlyCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHighlyCreterion", value: "Not Met" })}>
            <input name="asdHighlyCreterion" type="checkbox" value="Not Met" checked={asdContent.asdHighlyCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>
        <div className="mt-3">
          4. Hyper- or hypo-reactivity to sensory input or unusual interest in sensory aspects of environment (such as apparent indifference to pain/temperature, adverse response to specific sounds or textures, excessive smelling or touching of objects, visual fascination with lights or movement).
        </div>
        <div className="mt-2">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={4}
            name="asdHyper"
            value={asdContent.asdHyper}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
        <div className="fw-bold mt-1 d-flex align-items-center gap-3">
          <div>Criterion</div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHyperCreterion", value: "Met" })}>
            <input name="asdHyperCreterion" type="checkbox" value="Met" checked={asdContent.asdHyperCreterion === "Met"} readOnly />
            <div>Met</div>
          </div>
          <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHyperCreterion", value: "Not Met" })}>
            <input name="asdHyperCreterion" type="checkbox" value="Not Met" checked={asdContent.asdHyperCreterion === "Not Met"} readOnly />
            <div>Not Met</div>
          </div>
        </div>
        <div className="mt-3">
          <div>C. Symptoms must be present in the early developmental period (but may not become fully manifest until social demands exceed limited capacities, or may be masked by learned strategies later in life)</div>
          <div className="fw-bold mt-1 d-flex align-items-center gap-3">
            <div>Criterion</div>
            <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSymptomsC", value: "Met" })}>
              <input name="asdSymptomsC" type="checkbox" value="Met" checked={asdContent.asdSymptomsC === "Met"} readOnly />
              <div>Met</div>
            </div>
            <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSymptomsC", value: "Not Met" })}>
              <input name="asdSymptomsC" type="checkbox" value="Not Met" checked={asdContent.asdSymptomsC === "Not Met"} readOnly />
              <div>Not Met</div>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <div>D. Symptoms cause clinically significant impairment in social, occupational, or other important areas of current functioning.</div>
          <div className="fw-bold mt-1 d-flex align-items-center gap-3">
            <div>Criterion</div>
            <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSymptomsD", value: "Met" })}>
              <input name="asdSymptomsD" type="checkbox" value="Not Met" checked={asdContent.asdSymptomsD === "Met"} readOnly/>
              <div>Met</div>
            </div>
            <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSymptomsD", value: "Not Met" })}>
              <input name="asdSymptomsD" type="checkbox" value="Met" checked={asdContent.asdSymptomsD === "Not Met"} readOnly/>
              <div>Not Met</div>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <div>E. These disturbances are not better explained by intellectual disability or global developmental delay.</div>
          <div className="fw-bold mt-1 d-flex align-items-center gap-3">
            <div>Criterion</div>
            <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSymptomsE", value: "Met" })}>
              <input name="asdSymptomsE" type="checkbox" value="Met" checked={asdContent.asdSymptomsE === "Met"} readOnly />
              <div>Met</div>
            </div>
            <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSymptomsE", value: "Not Met" })}>
              <input name="asdSymptomsE" type="checkbox" value="Not Met" checked={asdContent.asdSymptomsE === "Not Met"} readOnly />
              <div>Not Met</div>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <table style={{ margin: "0px auto" }}>
            <tbody>
              <tr style={{ border: '1px solid' }}>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center">
                    <div>Section A (Severity </div>
                    <select
                      name="SectionLeft"
                      style={{ border: 'none', borderBottom: '1px solid black', outline: 'none', padding: '0px 5px', }}
                      onChange={(e) => onASDChange(e.target)}
                      value={asdContent.SectionLeft}
                    >
                      <option value="Level 1">Level 1</option>
                      <option value="Level 2">Level 2</option>
                      <option value="Level 3">Level 3</option>
                    </select>
                    <div>)</div>
                  </div>
                </td>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center">
                    <div>Section B (Severity </div>
                    <select
                      name="SectionRight"
                      style={{ border: 'none', borderBottom: '1px solid black', outline: 'none', padding: '0px 5px' }}
                      onChange={(e) => onASDChange(e.target)}
                      value={asdContent.SectionRight}
                    >
                      <option value="Level 1">Level 1</option>
                      <option value="Level 2">Level 2</option>
                      <option value="Level 3">Level 3</option>
                    </select>
                    <div>)</div>
                  </div>
                </td>
              </tr>
              <tr style={{ border: '1px solid' }}>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSocialCreterion", value: "Met" })}>
                      <input name="asdSectionLeftOne" type="checkbox" value="Met" checked={asdContent.asdSocialCreterion === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSocialCreterion", value: "Not Met" })}>
                      <input name="asdSectionLeftOne" type="checkbox" value="Not Met" checked={asdContent.asdSocialCreterion === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdStereotypedCreterion", value: "Met" })}>
                      <input name="asdSectionRightOne" type="checkbox" value="Met" checked={asdContent.asdStereotypedCreterion === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdStereotypedCreterion", value: "Not Met" })}>
                      <input name="asdSectionRightOne" type="checkbox" value="Not Met" checked={asdContent.asdStereotypedCreterion === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr style={{ border: '1px solid' }} className="px-4 py-2">
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdNonverbalCreterion", value: "Met" })}>
                      <input name="asdSectionLeftTwo" type="checkbox" value="Met" checked={asdContent.asdNonverbalCreterion === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdNonverbalCreterion", value: "Not Met" })}>
                      <input name="asdSectionLeftTwo" type="checkbox" value="Not Met" checked={asdContent.asdNonverbalCreterion === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdInsistenceCreterion", value: "Met" })}>
                      <input name="asdSectionRightTwo" type="checkbox" value="Met" checked={asdContent.asdInsistenceCreterion === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdInsistenceCreterion", value: "Not Met" })}>
                      <input name="asdSectionRightTwo" type="checkbox" value="Not Met" checked={asdContent.asdInsistenceCreterion === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr style={{ border: '1px solid' }} className="px-4 py-2">
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdDevelopingCreterion", value: "Met" })}>
                      <input name="asdSectionLeftThree" type="checkbox" value="Met" checked={asdContent.asdDevelopingCreterion === "Met"} readOnly/>
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdDevelopingCreterion", value: "Not Met" })}>
                      <input name="asdSectionLeftThree" type="checkbox" value="Not Met" checked={asdContent.asdDevelopingCreterion === "Not Met"} readOnly/>
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHighlyCreterion", value: "Met" })}>
                      <input name="asdSectionRightThree" type="checkbox" value="Met" checked={asdContent.asdHighlyCreterion === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHighlyCreterion", value: "Not Met" })}>
                      <input name="asdSectionRightThree" type="checkbox" value="Not Met" checked={asdContent.asdHighlyCreterion === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr style={{ border: '1px solid' }} className="px-4 py-2">
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  {/* <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSectionLeftFour", value: "Met" })}>
                      <input name="asdSectionLeftFour" type="checkbox" value="Met" checked={asdContent.asdSectionLeftFour === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdSectionLeftFour", value: "Not Met" })}>
                      <input name="asdSectionLeftFour" type="checkbox" value="Not Met" checked={asdContent.asdSectionLeftFour === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div> */}
                </td>
                <td style={{ border: '1px solid' }} className="px-4 py-2">
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHyperCreterion", value: "Met" })}>
                      <input name="asdSectionRightFour" type="checkbox" value="Met" checked={asdContent.asdHyperCreterion === "Met"} readOnly />
                      <div>Met</div>
                    </div>
                    <div className="d-flex align-items-center gap-1" style={{ cursor: 'pointer' }} onClick={() => onASDChange({ name: "asdHyperCreterion", value: "Not Met" })}>
                      <input name="asdSectionRightFour" type="checkbox" value="Not Met" checked={asdContent.asdHyperCreterion === "Not Met"} readOnly />
                      <div>Not Met</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="w-100">
        <div className="fw-bold">Summary:</div>
        <div className="mt-2 w-100">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={7}
            name="asdSummary"
            value={asdContent.asdSummary}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
      </div>
      <div className="w-100">
        <div className="fw-bold">Follow-Up:</div>
        <div className="mt-2 w-100">
          <textarea
            style={{ outline: 'none', resize: 'none', borderRadius: '4px', background: 'none' }}
            className="w-100 py-1 px-2"
            rows={7}
            name="asdFollowUp"
            value={asdContent.asdFollowUp}
            onChange={(e) => onASDChange(e.target)}
          />
        </div>
      </div>

      {
        rewriteStatus === true && <div className="mt-4 w-100">
          <div className="fw-bold">Recommendations</div>
          <div className="mt-2">
            <textarea
              style={{ outline: 'none', resize: 'none', borderRadius: '4px' }}
              className="w-100 py-1 px-2"
              rows={7}
              name="additionalRecommendations"
              value={asdContent.additionalRecommendations}
              onChange={onASDChange}
            />
          </div>
        </div>
      }

      <div className="lh-lg fw-bold mt-5">
        <div>Kind regards,</div>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ border: "1px dashed", width: '250px', height: '90px', cursor: 'pointer' }}
          onClick={() => signatureInputRef.current?.click()}>
          <div>
            {signUploaded
              ? <img
                src={signUrl}
                alt="Uploaded logo"
                style={{ width: signDimensions.width, height: signDimensions.height }}
              />
              : <Plus />}
          </div>
          <input
            ref={signatureInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: "none" }}
            onChange={(e) => handleUpload(e, "sign")}
          />
        </div>
        <div>{fullName}</div>
        <div>Registered Psychologist</div>
      </div>
    </div>
  )
}

export default ASDTemplate;